import { useEffect } from 'react';
import ModuleCard from './components/ModuleCard';
import styles from './ModuleSelectionPage.module.scss';
import { ApplicationRoutes } from '../../../routes';
import LandingUserInfoDropdown from '../../../common/components/user-info-dropdown/LandingUserInfoDropDown';
import { hasAnyPermission } from '../../../guards/common/authentification-functions';
import { UtilityPermissions } from '../../../model/permissions/permissions';
import { useTenant } from '../../../tenant/branding/useTenant';
import { TenantLogo } from '../../../tenant/branding/TenantLogo';
import { useFeatureFlags } from '../../../tenant/features/useFeatureFlag';
import {
	BILLS_PAYMENT_OPERATIONS_ADMIN_FLAGS,
	SERVICE_REQUEST_ADMIN_FLAGS,
	SUPPORT_DESK_ADMIN_FLAGS,
} from '../../../tenant/features/featureFlags';

const ModuleSelectionPage = () => {
	const { logo, name } = useTenant();
	const { isEnabled, refresh: refreshFeatureFlags } = useFeatureFlags();
	// An admin may have just changed the utility's feature configuration; re-read the
	// flags every time this page opens so the module cards reflect the current state
	// rather than whatever was fetched at app start.
	useEffect(() => {
		void refreshFeatureFlags();
	}, [refreshFeatureFlags]);
	// A module is locked by permission or by feature flag: with every flag in its
	// group off there is nothing behind the card to open. While the flags are still
	// loading they all read false, so the card starts locked and unlocks once the
	// SDK confirms — the safe direction.
	const hasAnyFeature = (flags: readonly string[]) => flags.some(isEnabled);
	const modules = [
		{
			title: "Bills Payment and Operations Management",
			description: "Simplify revenue management, customer interactions, and billing with our comprehensive commercial module.",
			image: '/images/bills_payment_and_operation_management.jpg',
			type: 'bills',
			link: `/${ApplicationRoutes.ADMIN_UTIL_BASE}/${ApplicationRoutes.BILLS_OPERATIONS}/${ApplicationRoutes.DASHBOARD}`,
			locked:
				!hasAnyPermission([UtilityPermissions.ViewDashboard]) ||
				!hasAnyFeature(BILLS_PAYMENT_OPERATIONS_ADMIN_FLAGS),
		},
		{
			title: "Client Management",
			description: "Distributed Energy Resources Management System optimizes, controls, and monitors distributed energy for efficient use.",
			image: '/images/client_management.jpg',
			type: 'client-management',
			link: `/${ApplicationRoutes.ADMIN_UTIL_BASE}/${ApplicationRoutes.CLIENT_MANAGEMENT}/${ApplicationRoutes.CUSTOMER_360}`,
			locked: true,
		},
		{
			title: "Service Request",
			description: "Easily track and manage requests for support, maintenance, or issue resolution",
			image: '/images/service_requests_img.jpg',
			type: 'service-request',
			link: `/${ApplicationRoutes.ADMIN_UTIL_BASE}/${ApplicationRoutes.SERVICE_REQUEST_MODULE}/${ApplicationRoutes.DASHBOARD}`,
			locked:
				!hasAnyPermission([
					UtilityPermissions.AddNewUtilityUser,
					UtilityPermissions.ViewUtilityUsers,
					UtilityPermissions.ViewAuditLogs,
					UtilityPermissions.ViewUtilitySettings,
				]) || !hasAnyFeature(SERVICE_REQUEST_ADMIN_FLAGS),
		},
		{
			title: "Support Desk",
			description: "Seamless management of utility assets for improved efficiency",
			image: '/images/support_desk.jpg',
			type: 'customer-support',
			link: `/${ApplicationRoutes.ADMIN_UTIL_BASE}/${ApplicationRoutes.CUSTOMER_SUPPORT}`,
			locked:
				!hasAnyPermission([UtilityPermissions.ViewServiceRequestSupportDesk]) ||
				!hasAnyFeature(SUPPORT_DESK_ADMIN_FLAGS),
		}
	];

	return (
		<div className={styles.container}>
			{/* Header */}
			<div className={styles.header}>
				<div className={styles.logoContainer}>
					<TenantLogo logo={logo} alt={name} className={styles.logo} />
					<h3 className={styles.headerName}>{name}</h3>
				</div>
				<LandingUserInfoDropdown />
			</div>

			<h1 className={styles.heading}>
				What would you like to do today?
			</h1>

			{/* Main content */}
			<div className={styles.main}>
				<div className={styles.grid}>
					{modules.map((module, index) => (
						<ModuleCard
							type={module.type}
							link={module.link}
							key={index}
							title={module.title}
							description={module.description}
							image={module.image}
							locked={module.locked}
						/>
					))}
				</div>
			</div>

			{/* Footer */}
			<div className={styles.footer}>
				Powered by <span className={styles.poweredBy}>Beacon Power Services</span>
			</div>
		</div>
	);
};

export default ModuleSelectionPage;
